// Client API — proxy Worker teogest-api.mhfreehome.workers.dev
// Types alignes sur les vrais retours Teogest (verifies 2026-07-16 apres deblocage API).

export const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  'https://teogest-api.mhfreehome.workers.dev'

// ─── Types Teogest (retours reels, pas la doc) ──────────────

export type Societe = {
  IdSociete: number
  CodeClient: string
  RaisonSociale: string
  Siren: string | null
  Siret: string | null
  FormeJuridiqueAbregee: string | null
  CodeNAF: string | null
  Adresse1: string | null
  CodePostal: string | null
  Ville: string | null
  Pays: string | null
  TvaIntra: string | null
  TypeSociete: string | null
  Descriptif: string | null
  Email: string | null
  Telephone: string | null
  Mobile: string | null
  Prenom: string | null
  Nom: string | null
  DateCreation: string | null
  DateArchivageAppliquee: string | null
  IdentifiantExterne: string | null
  MoisDeCloture: number | null
}

export type DossierAnnuel = {
  IdDossierAnnuel: number
  CodeClient: string
  Libelle: string
  DateDebut: string
  DateFin: string
  DateDebutSaisie: string
  DateFinSaisie: string
  DateCloture: string | null
  EnCours: string // "true"/"false" texte (Teogest le renvoie comme string)
  IdDossierPrecedent: number | null
  LibelleDossierPrecedent: string | null
  Descriptif: string | null
}

// Ecriture — champs FEC standard renvoyes par Teogest (peuvent varier)
export type Ecriture = {
  IdEcriture?: number
  JournalCode: string
  JournalLib?: string
  EcritureNum?: string
  EcritureDate: string
  CompteNum: string
  CompteLib?: string
  CompAuxNum?: string
  CompAuxLib?: string
  PieceRef?: string
  PieceDate?: string
  EcritureLib: string
  Debit?: number | null
  Credit?: number | null
  EcritureLet?: string
  ValidDate?: string
  Origine?: string
}

export type AuditRow = {
  id: number
  ts: number
  method: string
  path: string
  code_client: string | null
  status: number | null
  duration_ms: number
  write_mode: number
  error: string | null
}

export type Health = {
  ok: boolean
  version: string
  writeMode: boolean
  writeModeExpiresAt: number | null
  writeModeExpiresInMs: number | null
  teogestReachable?: boolean
  dbReachable?: boolean
}

// ─── Fetch typé ────────────────────────────────────────────────

type ApiOk<T> = { ok: true; status: number; data: T }
type ApiErr = { ok: false; status?: number; error: string }
type ApiResult<T> = ApiOk<T> | ApiErr

async function get<T>(path: string): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`${API_BASE}${path}`, { headers: { Accept: 'application/json' } })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json?.ok === false) {
      return { ok: false, status: res.status, error: json?.error ?? `HTTP ${res.status}` }
    }
    return { ok: true, status: res.status, data: (json.data ?? json) as T }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) }
  }
}

// ─── Endpoints exposés ─────────────────────────────────────────

export const api = {
  health: () => get<Health>('/health'),
  societes: () => get<Societe[]>('/api/societes'),
  societe: (code: string) => get<Societe>(`/api/societes/${encodeURIComponent(code)}`),
  dossiersAnnuels: (code: string) =>
    get<DossierAnnuel[]>(`/api/societes/${encodeURIComponent(code)}/dossiers-annuels`),
  ecritures: (code: string, dossierId: number | string) =>
    get<Ecriture[]>(
      `/api/societes/${encodeURIComponent(code)}/dossiers-annuels/${encodeURIComponent(String(dossierId))}/ecritures`
    ),
  audit: (limit = 100) => get<AuditRow[]>(`/api/audit?limit=${limit}`),
}

// ─── Utilitaires ───────────────────────────────────────────────

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function fmtDateTime(ts: number | string | null | undefined): string {
  if (ts == null) return '—'
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts)
  if (isNaN(d.getTime())) return String(ts)
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  })
}

export function fmtMoney(n: number | null | undefined): string {
  if (n == null || isNaN(Number(n))) return '—'
  return Number(n).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }) + ' €'
}

export function fmtSiren(s: string | null | undefined): string {
  if (!s) return '—'
  const clean = s.replace(/\D/g, '')
  if (clean.length !== 9) return s
  return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6, 9)}`
}
